import { useState, useCallback, useRef } from 'react';
import { fetchHint, MAX_HINT_LEVEL } from './hint-service';
import { GEMINI_API_KEY as DEFAULT_API_KEY } from './config';

const GEMINI_API_KEY_STORAGE_KEY = 'fcc-chatbot-gemini-key';

/**
 * Custom hook that manages the progressive hints lifecycle.
 *
 * Returns:
 *   hints         – array of { level, text } objects
 *   currentLevel  – next hint level that will be requested
 *   loading       – whether a hint is being fetched
 *   error         – latest error message or null
 *   requestHint   – function to fetch the next hint
 *   resetHints    – function to clear all hints (e.g. when challenge changes)
 *   apiKey        – current API key
 *   setApiKey     – persist a new API key
 */
export default function useHints() {
  const [hints, setHints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const levelRef = useRef(1);

  // --------------- API key helpers ---------------
  // Priority: localStorage override → config.js default → env variable
  const readKey = () => {
    try {
      return (
        localStorage.getItem(GEMINI_API_KEY_STORAGE_KEY) || DEFAULT_API_KEY || ''
      );
    } catch {
      return DEFAULT_API_KEY || '';
    }
  };

  const [apiKey, setApiKeyState] = useState(readKey);

  const setApiKey = useCallback(key => {
    try {
      localStorage.setItem(GEMINI_API_KEY_STORAGE_KEY, key);
    } catch {
      // storage might be blocked
    }
    setApiKeyState(key);
  }, []);

  // --------------- Hint request ---------------
  /**
   * @param {object} challengeCtx  – { title, description, instructions, tests, code }
   */
  const requestHint = useCallback(
    async challengeCtx => {
      const key = readKey();
      if (!key || key === 'YOUR_GEMINI_API_KEY_HERE') {
        setError(
          'Gemini API key not configured. Set it in config.js or via the ⚙️ button.'
        );
        return;
      }

      setLoading(true);
      setError(null);

      const level = levelRef.current;

      try {
        const text = await fetchHint({
          apiKey: key,
          ...challengeCtx,
          level
        });

        setHints(prev => [...prev, { level, text }]);
        levelRef.current = Math.min(level + 1, MAX_HINT_LEVEL);
      } catch (err) {
        setError('Oops, Please try again');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // --------------- Reset ---------------
  const resetHints = useCallback(() => {
    setHints([]);
    setError(null);
    levelRef.current = 1;
  }, []);

  return {
    hints,
    currentLevel: levelRef.current,
    maxLevel: MAX_HINT_LEVEL,
    loading,
    error,
    requestHint,
    resetHints,
    apiKey,
    setApiKey
  };
}
